import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ActivityStatusClient, ActivityStatusDTO, ActivityStatusSearchRequestDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new ActivityStatusClient(API_BASE_URL, axiosInstance);

// Cached hook for all active activity statuses (used in dropdowns/lookups)
export const useActivityStatuses = () => {
  return useQuery({
    queryKey: ['activityStatuses', 'all'],
    queryFn: async () => {
      const response = await apiClient.searchActivityStatuses({
        filters: { isActive: true },
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Non-cached search hook for admin pages with filters
export const useActivityStatusSearch = () => {
  const search = async (params: any) => {
    const response = await apiClient.searchActivityStatuses(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

// Get single activity status by ID
export const useActivityStatusById = (id?: string) => {
  return useQuery({
    queryKey: ['activityStatuses', id],
    queryFn: async () => {
      if (!id) return null;
      return await apiClient.getActivityStatusById(id);
    },
    enabled: !!id,
  });
};

// Create mutation
export const useCreateActivityStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ActivityStatusDTO) => {
      return await apiClient.createActivityStatus(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activityStatuses'] });
    },
  });
};

// Update mutation
export const useUpdateActivityStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ActivityStatusDTO }) => {
      return await apiClient.updateActivityStatus(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activityStatuses'] });
    },
  });
};

// Delete mutation
export const useDeleteActivityStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await apiClient.deleteActivityStatus(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activityStatuses'] });
    },
  });
};